import { List, Map } from 'immutable';
import { map, set } from 'lodash';
import uniqid from 'uniqid';
import { isDividendsIncome } from '../../components/NDFL3/private/forms/IncomesForm/IncomesFromRFComponent/IncomeService';
import { formatNumber } from '../../../../utils/format';
import NDFL3YearLogic from '../../components/NDFL3/services/NDFL3YearsLogic/NDFL3YearLogic';

export function setIdForItem(item = {}, prefix = 'default') {
  if (item instanceof Map) {
    return item.set('id', uniqid(prefix));
  } else {
    set(item, 'id', uniqid(prefix));
    return item;
  }
}

export function setIdsForArray(array, prefix) {
  if (array instanceof List) {
    return array.map((item) => {
      return setIdForItem(item, prefix);
    });
  } else {
    return map(array, (item) => {
      return setIdForItem(item, prefix);
    });
  }
}

/**
 *
 * @param formSources - income sources
 * @param sourceTaxRate - common tax rate for source
 * @param setTaxableIncomeSum - set 'taxableIncomeSum' field from source - taxableIncomeSumWithDividends and
 * taxAmountCalculatedWithDividends fields will be set based on 'taxableIncomeSum' field
 * @returns {*}
 */
export function calculateSourceTaxes(formSources, sourceTaxRate, setTaxableIncomeSum) {
  /**
   *  agentDeductions field includes additional deductions
   */
  const agentDeductionsIncomeCodes = [1544, 1545, 1530, 1531];
  return formSources.map((source) => {
    const incomes = source.get('incomes', List());

    let {
      totalTax,
      totalTaxDeductionsSum,
      totalIncomeSum,
      totalDividendsSum,
      totalDividendsTaxDeduction,
      totalDividendsTax,
      totalDividendsWithheldTax,
    } = incomes.reduce(
      (res, income) => {
        let {
          totalTax,
          totalTaxDeductionsSum,
          totalIncomeSum,
          totalDividendsSum,
          totalDividendsTaxDeduction,
          totalDividendsTax,
          totalDividendsWithheldTax,
        } = res;
        const incomeTypeCode = +income.get('incomeTypeCodeASVK');
        const incomeSum = +income.get('incomeSum', 0);
        const deductionSum = +income.get('deductionSum', 0);
        const taxRate = sourceTaxRate || +income.get('taxRate', 0);

        if (
          !(
            isDividendsIncome(incomeTypeCode) &&
            NDFL3YearLogic.logic.showSpecialFieldsForDividendsIncome()
          )
        ) {
          const shareOfExpenses = +income.get('shareOfExpenses', 0);
          const expensesToManagingComprades = +income.get('expensesToManagingComprades', 0);
          const lossOfPastYears = +income.get('lossOfPastYears', 0);
          const depositSum = +income.get('depositSum', 0);
          let agentDeductionSum = 0;

          if (agentDeductionsIncomeCodes.includes(incomeTypeCode)) {
            agentDeductionSum = income
              .get('agentDeductions', List())
              .reduce((acc, current) => (current ? acc + current.get('sum', 0) : acc), 0);
          }

          totalIncomeSum += incomeSum;

          const taxDeductionSum =
            incomeSum -
            deductionSum -
            (shareOfExpenses +
              expensesToManagingComprades +
              lossOfPastYears +
              depositSum +
              agentDeductionSum);

          totalTaxDeductionsSum = totalTaxDeductionsSum + taxDeductionSum;
          totalTax = totalTax + (taxDeductionSum * taxRate) / 100;
        } else {
          const dividendSum = +income.get('dividendSum', 0);
          const withheldTax = +income.get('withheldTax', 0);
          const calculatedTax = +income.get('calculatedTax', 0);

          totalDividendsSum += dividendSum;
          totalDividendsWithheldTax += withheldTax;
          totalDividendsTaxDeduction = totalDividendsTaxDeduction + (dividendSum - deductionSum);
          totalDividendsTax += calculatedTax;
        }

        return {
          totalTax: totalTax < 0 ? 0 : totalTax,
          totalTaxDeductionsSum: totalTaxDeductionsSum < 0 ? 0 : totalTaxDeductionsSum,
          totalIncomeSum,
          totalDividendsSum,
          totalDividendsTaxDeduction:
            totalDividendsTaxDeduction < 0 ? 0 : totalDividendsTaxDeduction,
          totalDividendsTax,
          totalDividendsWithheldTax,
        };
      },
      {
        totalTax: 0,
        totalTaxDeductionsSum: 0,
        totalIncomeSum: 0,
        totalDividendsSum: 0,
        totalDividendsTaxDeduction: 0,
        totalDividendsTax: 0,
        totalDividendsWithheldTax: 0,
      },
    );

    totalTax = Math.round(formatNumber(totalTax));

    /**
     * virtual fields are using only on client, BE side does not handle it
     */
    return source
      .set('incomeSum', totalIncomeSum)
      .set('taxableIncomeSum', formatNumber(totalTaxDeductionsSum))
      .set('taxAmountCalculated', totalTax)
      .set('incomeSumWithDividends', formatNumber(totalIncomeSum + totalDividendsSum)) // virtual field
      .set(
        'taxableIncomeSumWithDividends',
        setTaxableIncomeSum
          ? source.get('taxableIncomeSum')
          : formatNumber(totalTaxDeductionsSum + totalDividendsTaxDeduction),
      ) // virtual field
      .set('taxableDividendsSum', formatNumber(totalDividendsTaxDeduction)) // virtual field
      .set(
        'taxAmountCalculatedWithDividends',
        setTaxableIncomeSum
          ? (source.get('taxableIncomeSum') * source.get('taxRate')) / 100
          : totalTax + totalDividendsTax,
      ) // virtual field
      .set(
        'taxAmountWithheldWithDividends',
        source.get('taxAmountWithheld', 0) + totalDividendsWithheldTax,
      ) // virtual field
      .set('dividendsWithheldTax', totalDividendsWithheldTax); // virtual field
  });
}
